import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  UgcCreateMediaSubmissionRequest,
  UgcCreateXploreSubmissionRequest,
  UgcEligibilityResponse,
  UgcNameCheckResponse,
  UgcSubmissionDTO
} from '../shared/object/xplorer-be-parsed-classes';
import { TokenStorageService } from './auth/token-storage.service';

interface UgcCacheEntry<T> {
  value: T;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class UgcService {
  private static readonly ELIGIBILITY_TTL_MS = 30_000;
  private static readonly NAME_CHECK_TTL_MS = 120_000;
  private static readonly MINE_TTL_MS = 20_000;
  private static readonly MAX_NAME_CHECK_CACHE_ENTRIES = 50;

  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly baseUrl = `${environment.API_URL}/ugc`;
  private eligibilityCache: UgcCacheEntry<UgcEligibilityResponse> | null = null;
  private eligibilityInFlight$: Observable<UgcEligibilityResponse> | null = null;
  private mineCache: UgcCacheEntry<UgcSubmissionDTO[]> | null = null;
  private mineInFlight$: Observable<UgcSubmissionDTO[]> | null = null;
  private readonly nameCheckCache = new Map<string, UgcCacheEntry<UgcNameCheckResponse>>();
  private readonly nameCheckInFlight = new Map<string, Observable<UgcNameCheckResponse>>();
  private cacheScopeKey: string | null = null;

  getEligibility(forceRefresh = false): Observable<UgcEligibilityResponse> {
    this.syncCacheScope();

    if (!forceRefresh) {
      const cachedResponse = this.getValidEntry(this.eligibilityCache);
      if (cachedResponse) {
        return of(cachedResponse.value);
      }

      if (this.eligibilityInFlight$) {
        return this.eligibilityInFlight$;
      }
    }

    let request$: Observable<UgcEligibilityResponse>;
    request$ = this.http.get<UgcEligibilityResponse>(`${this.baseUrl}/eligibility`).pipe(
      tap((response) => this.setEligibilityCache(response)),
      finalize(() => {
        if (this.eligibilityInFlight$ === request$) {
          this.eligibilityInFlight$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.eligibilityInFlight$ = request$;
    return request$;
  }

  acceptProgram(): Observable<UgcEligibilityResponse> {
    this.syncCacheScope();

    return this.http.post<UgcEligibilityResponse>(`${this.baseUrl}/program/accept`, {}).pipe(
      tap((response) => {
        this.eligibilityInFlight$ = null;
        this.setEligibilityCache(response);
      })
    );
  }

  nameCheck(territoryCode: string, name: string, forceRefresh = false): Observable<UgcNameCheckResponse> {
    this.syncCacheScope();
    this.pruneExpiredNameCheckCache();

    const sanitizedTerritoryCode = territoryCode.trim();
    const sanitizedName = this.sanitizeNameCheckInput(name);
    const cacheKey = this.buildNameCheckCacheKey(sanitizedTerritoryCode, sanitizedName);
    const params = new HttpParams()
      .set('territoryCode', sanitizedTerritoryCode)
      .set('name', sanitizedName);

    if (!forceRefresh && sanitizedName) {
      const cachedResponse = this.getValidEntry(this.nameCheckCache.get(cacheKey));
      if (cachedResponse) {
        return of(cachedResponse.value);
      }

      const inFlightRequest = this.nameCheckInFlight.get(cacheKey);
      if (inFlightRequest) {
        return inFlightRequest;
      }
    }

    let request$: Observable<UgcNameCheckResponse>;
    request$ = this.http.get<UgcNameCheckResponse>(`${this.baseUrl}/name-check`, { params }).pipe(
      tap((response) => {
        if (sanitizedName) {
          this.setNameCheckCache(cacheKey, response);
        }
      }),
      finalize(() => {
        if (sanitizedName && this.nameCheckInFlight.get(cacheKey) === request$) {
          this.nameCheckInFlight.delete(cacheKey);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    if (sanitizedName) {
      this.nameCheckInFlight.set(cacheKey, request$);
    }

    return request$;
  }

  getMySubmissions(forceRefresh = false): Observable<UgcSubmissionDTO[]> {
    this.syncCacheScope();

    if (!forceRefresh) {
      const cachedResponse = this.getValidEntry(this.mineCache);
      if (cachedResponse) {
        return of(cachedResponse.value);
      }

      if (this.mineInFlight$) {
        return this.mineInFlight$;
      }
    }

    let request$: Observable<UgcSubmissionDTO[]>;
    request$ = this.http.get<UgcSubmissionDTO[]>(`${this.baseUrl}/submissions/mine`).pipe(
      tap((response) => this.setMineCache(response)),
      finalize(() => {
        if (this.mineInFlight$ === request$) {
          this.mineInFlight$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.mineInFlight$ = request$;
    return request$;
  }

  submitNewXplore(request: UgcCreateXploreSubmissionRequest): Observable<UgcSubmissionDTO> {
    this.syncCacheScope();

    return this.http.post<UgcSubmissionDTO>(`${this.baseUrl}/submissions/xplore`, request).pipe(
      tap(() => {
        this.invalidateEligibilityCache();
        this.invalidateMineCache();
      })
    );
  }

  submitExistingXploreMedia(xploreId: number, request: UgcCreateMediaSubmissionRequest): Observable<UgcSubmissionDTO> {
    this.syncCacheScope();

    return this.http.post<UgcSubmissionDTO>(`${this.baseUrl}/submissions/xplore/${xploreId}/media`, request).pipe(
      tap(() => {
        this.invalidateEligibilityCache();
        this.invalidateMineCache();
      })
    );
  }

  private syncCacheScope(): void {
    const currentScopeKey = this.resolveCacheScopeKey();
    if (this.cacheScopeKey === currentScopeKey) {
      return;
    }

    this.cacheScopeKey = currentScopeKey;
    this.clearAllCache();
  }

  private resolveCacheScopeKey(): string {
    const user = this.tokenStorage.getUser();
    if (user?.keycloakId) {
      return `keycloak:${user.keycloakId}`;
    }

    if (user?.userId) {
      return `user:${user.userId}`;
    }

    const tokenPayload = this.tokenStorage.getTokenPayload(this.tokenStorage.getToken());
    if (tokenPayload?.sub) {
      return `subject:${tokenPayload.sub}`;
    }

    return 'guest';
  }

  private clearAllCache(): void {
    this.invalidateEligibilityCache();
    this.invalidateMineCache();
    this.nameCheckCache.clear();
    this.nameCheckInFlight.clear();
  }

  private invalidateEligibilityCache(): void {
    this.eligibilityCache = null;
    this.eligibilityInFlight$ = null;
  }

  private invalidateMineCache(): void {
    this.mineCache = null;
    this.mineInFlight$ = null;
  }

  private setEligibilityCache(response: UgcEligibilityResponse): void {
    this.eligibilityCache = this.createCacheEntry(response, UgcService.ELIGIBILITY_TTL_MS);
  }

  private setMineCache(response: UgcSubmissionDTO[]): void {
    this.mineCache = this.createCacheEntry(response, UgcService.MINE_TTL_MS);
  }

  private setNameCheckCache(cacheKey: string, response: UgcNameCheckResponse): void {
    this.nameCheckCache.delete(cacheKey);
    this.nameCheckCache.set(cacheKey, this.createCacheEntry(response, UgcService.NAME_CHECK_TTL_MS));
    this.evictOldNameCheckEntries();
  }

  private createCacheEntry<T>(value: T, ttlMs: number): UgcCacheEntry<T> {
    return {
      value,
      expiresAt: Date.now() + ttlMs
    };
  }

  private getValidEntry<T>(entry: UgcCacheEntry<T> | null | undefined): UgcCacheEntry<T> | null {
    if (!entry) {
      return null;
    }

    if (entry.expiresAt <= Date.now()) {
      return null;
    }

    return entry;
  }

  private sanitizeNameCheckInput(name: string): string {
    return name.trim().replace(/\s+/g, ' ');
  }

  private buildNameCheckCacheKey(territoryCode: string, name: string): string {
    return `${territoryCode.trim().toLowerCase()}::${name.toLowerCase()}`;
  }

  private pruneExpiredNameCheckCache(): void {
    const now = Date.now();
    for (const [cacheKey, entry] of this.nameCheckCache.entries()) {
      if (entry.expiresAt <= now) {
        this.nameCheckCache.delete(cacheKey);
      }
    }
  }

  private evictOldNameCheckEntries(): void {
    this.pruneExpiredNameCheckCache();

    while (this.nameCheckCache.size > UgcService.MAX_NAME_CHECK_CACHE_ENTRIES) {
      const oldestKey = this.nameCheckCache.keys().next().value;
      if (!oldestKey) {
        return;
      }

      this.nameCheckCache.delete(oldestKey);
    }
  }
}
